import { useEffect, useRef } from 'react';
import { Fretboard } from '@moonwave99/fretboard.js';
import { fretboardConfiguration, colors } from '../config/fretboardConfig.js';

export function useFretboard() {
  const fretboardRef = useRef(null);

  useEffect(() => {
    try {
      fretboardRef.current = new Fretboard({
        ...fretboardConfiguration,
        dotText: ({ note }) => note,
        dotFill: ({ interval, inBox }) =>
          inBox ? (interval === '1P' ? colors.defaultActiveFill : colors.defaultFill) : colors.disabled,
      });
    } catch (error) {
      console.error('Failed to initialize fretboard:', error);
    }
  }, []);


  const renderScale = (params) => {
    if (!fretboardRef.current) {
      console.warn('Fretboard not initialized');
      return;
    }

    try {
      // console.log('Calling renderScale with:', JSON.stringify(params, null, 2));
      fretboardRef.current.renderScale(params);
    } catch (error) {
      console.error('Render scale error:', error);
    }
  };

  const updateStyle = (styleConfig) => {
    if (!fretboardRef.current) {
      return;
    }

    try {
      fretboardRef.current.style(styleConfig);
    } catch (error) {
      console.error('Failed to update fretboard style:', error);
    }
  };

  // Style functions
  const stylePresets = {
    showNotes: () =>
      updateStyle({
        text: ({ note }) => note,
        fill: 'white',
      }),

    showNotesWithOctave: () =>
      updateStyle({
        text: ({ note, octave }) => `${note}${octave}`,
        fill: ({ octaveInScale }) => colors.octaves[octaveInScale],
      }),

    showAllIntervals: () =>
      updateStyle({
        text: ({ interval }) => interval,
        fill: ({ interval }) => colors.intervals[interval],
      }),

    highlightRoot: () =>
      updateStyle({
        text: ({ note }) => note,
        fill: ({ degree, interval }) => ([1].includes(degree) ? colors.intervals[interval] : colors.defaultFill),
      }),

    highlightRootTriad: () =>
      updateStyle({
        text: ({ degree, interval }) => ([1, 3, 5].includes(degree) ? interval : null),
        fill: ({ degree, interval }) =>
          [1, 3, 5].includes(degree) ? colors.intervals[interval] : colors.defaultFill,
      }),

    reset: () =>
      updateStyle({
        text: () => null,
        fill: colors.defaultFill,
        stroke: colors.defaultStroke,
      }),
  };

  return {
    renderScale,
    stylePresets,
    isInitialized: () => !!fretboardRef.current
  };
}
